<template>
  <div class="status-history-panel">
    <div class="box-title status-history-title">
      <h4>
        <i class="fal fa-history"></i>
        ປະຫວັດການປ່ຽນສະຖານະສາຂາ (Branch Status History)
      </h4>
      <vs-button transparent @click="$emit('back')">
        <i class="fal fa-arrow-left" :style="{ marginRight: '6px' }"></i>
        ກັບໄປຂໍ້ມູນສາຂາ
      </vs-button>
    </div>
    <p class="status-history-hint">
      ລາຍການທຸກຄັ້ງທີ່ມີການບັນທຶກແກ້ໄຂສາຂາ ແລ້ວ<b>ສະຖານະເປີດ-ປິດ</b> ຫຼື <b>ການປິດຖາວອນ</b> ປ່ຽນໄປ —
      ປ່ຽນຈາກຫຍັງເປັນຫຍັງ, ໃຜເປັນຜູ້ປ່ຽນ (ຊື່ / ອີເມວ / ລະຫັດພະນັກງານ) ແລະ ເວລາໃດ.
      ການແກ້ໄຂທີ່ບໍ່ໄດ້ແຕະສະຖານະຈະບໍ່ສະແດງຢູ່ນີ້.
    </p>

    <div v-if="loadError" class="notification is-warning" :style="{ marginTop: '10px' }">
      <i class="fal fa-exclamation-triangle" :style="{ marginRight: '6px' }"></i>
      ບໍ່ສາມາດໂຫຼດປະຫວັດໄດ້ — ລະບົບຫຼັງບ້ານອາດຍັງບໍ່ຮອງຮັບ ຫຼື ເກີດຂໍ້ຜິດພາດ. ກະລຸນາລອງໃໝ່ພາຍຫຼັງ.
      <vs-button transparent :style="{ display: 'inline-block', marginLeft: '8px' }" @click="fetchData()">
        ລອງໃໝ່
      </vs-button>
    </div>

    <div v-else class="columns is-multiline" :style="{ marginTop: '10px' }">
      <div class="column is-4">
        <div class="status-history-card">
          <label class="label">ສາຂາ</label>
          <p class="status-history-value">{{ branch.name || '-' }}</p>
          <p class="status-history-note">{{ branch.prefix || '' }}</p>
        </div>
      </div>
      <div class="column is-4">
        <div class="status-history-card" :class="currentStateClass">
          <label class="label">ສະຖານະປັດຈຸບັນ</label>
          <p class="status-history-value">
            <i :class="currentStateIcon" :style="{ marginRight: '6px' }"></i>
            {{ currentStateLabel }}
          </p>
          <p class="status-history-note" v-if="branch.is_permanently_closed && branch.closed_at">
            ປິດຖາວອນເມື່ອ {{ branch.closed_at }}
          </p>
          <p class="status-history-note" v-else-if="branch.status === 0 && branch.closed_at">
            ປິດຫຼ້າສຸດເມື່ອ {{ branch.closed_at }}
          </p>
          <p class="status-history-note" v-else-if="branch.status === 1 && branch.opened_at">
            ເປີດຫຼ້າສຸດເມື່ອ {{ branch.opened_at }}
          </p>
        </div>
      </div>
      <div class="column is-4">
        <div class="status-history-card">
          <label class="label">ຈຳນວນຄັ້ງທີ່ປ່ຽນສະຖານະ</label>
          <p class="status-history-value">{{ histories.length }}</p>
          <p class="status-history-note">ຈາກການບັນທຶກແກ້ໄຂທັງໝົດ {{ scanned }} ຄັ້ງ</p>
        </div>
      </div>
    </div>

    <div v-if="truncated && !loadError" class="notification is-light" :style="{ marginTop: '4px' }">
      <i class="fal fa-info-circle" :style="{ marginRight: '6px' }"></i>
      ສະແດງສະເພາະຈາກການບັນທຶກແກ້ໄຂ {{ scanned }} ຄັ້ງຫຼ້າສຸດ — ປະຫວັດເກົ່າກວ່ານັ້ນອາດບໍ່ສະແດງ.
    </div>

    <div class="status-history-table" v-if="!loadError">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>ລຳດັບ</vs-th>
            <vs-th>ວັນທີ-ເວລາ</vs-th>
            <vs-th>ການປ່ຽນແປງ</vs-th>
            <vs-th>ສະຖານະເປີດ-ປິດ (ຈາກ → ເປັນ)</vs-th>
            <vs-th>ປິດຖາວອນ (ຈາກ → ເປັນ)</vs-th>
            <vs-th>ຜູ້ປ່ຽນ</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="item.id" v-for="(item, index) in histories">
            <vs-td>{{ index + 1 }}</vs-td>
            <vs-td class="status-history-time">{{ item.changed_at || '-' }}</vs-td>
            <vs-td>
              <span class="status-history-kind" :class="eventKind(item).cls">
                <i :class="eventKind(item).icon" :style="{ marginRight: '4px' }"></i>
                {{ eventKind(item).label }}
              </span>
            </vs-td>
            <vs-td>
              <span :class="{ 'is-changed': item.changed_fields.includes('status') }">
                <span :class="statusClass(item.status.from)">{{ statusLabel(item.status.from) }}</span>
                <i class="fal fa-long-arrow-right status-history-arrow"></i>
                <span :class="statusClass(item.status.to)">{{ statusLabel(item.status.to) }}</span>
              </span>
              <p class="status-history-sub" v-if="item.changed_fields.includes('closed_at') && item.closed_at.to">
                ປິດເມື່ອ {{ item.closed_at.to }}
              </p>
              <p class="status-history-sub" v-if="item.changed_fields.includes('opened_at') && item.opened_at.to">
                ເປີດເມື່ອ {{ item.opened_at.to }}
              </p>
            </vs-td>
            <vs-td>
              <span :class="{ 'is-changed': item.changed_fields.includes('is_permanently_closed') }">
                <span :class="flagClass(item.is_permanently_closed.from)">{{ flagLabel(item.is_permanently_closed.from) }}</span>
                <i class="fal fa-long-arrow-right status-history-arrow"></i>
                <span :class="flagClass(item.is_permanently_closed.to)">{{ flagLabel(item.is_permanently_closed.to) }}</span>
              </span>
            </vs-td>
            <vs-td>
              <div class="status-history-user">
                <p class="status-history-user-name">
                  <i class="fal fa-user" :style="{ marginRight: '4px' }"></i>
                  {{ item.user.name || ('ຜູ້ໃຊ້ #' + item.user.id) }}
                </p>
                <p class="status-history-sub" v-if="item.user.email">
                  <i class="fal fa-envelope" :style="{ marginRight: '4px' }"></i>{{ item.user.email }}
                </p>
                <p class="status-history-sub" v-if="item.user.user_card_id">
                  <i class="fal fa-id-card" :style="{ marginRight: '4px' }"></i>ລະຫັດພະນັກງານ: {{ item.user.user_card_id }}
                </p>
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
      <p v-if="histories.length === 0 && !isLoading" class="status-history-empty">
        ຍັງບໍ່ມີປະຫວັດການປ່ຽນສະຖານະຂອງສາຂານີ້
      </p>
    </div>

    <Loading v-if="isLoading"/>
  </div>
</template>

<script>
import Loading from '@/views/Loading';

export default {
  components: { Loading },

  data: () => ({
    isLoading: false,
    loadError: false,
    branch: {
      id: null,
      name: '',
      prefix: '',
      status: null,
      is_permanently_closed: false,
      closed_at: null,
      opened_at: null,
    },
    histories: [],
    scanned: 0,
    truncated: false,
  }),

  computed: {
    branchId() {
      return this.$route.params.id;
    },
    currentStateLabel() {
      if (this.branch.is_permanently_closed) {
        return 'ປິດຖາວອນ';
      }
      if (this.branch.status === 0) {
        return 'ປິດຊົ່ວຄາວ';
      }
      if (this.branch.status === 1) {
        return 'ເປີດປົກກະຕິ';
      }
      return '-';
    },
    currentStateIcon() {
      if (this.branch.is_permanently_closed) {
        return 'fal fa-ban';
      }
      return this.branch.status === 0 ? 'fal fa-pause-circle' : 'fal fa-check-circle';
    },
    currentStateClass() {
      if (this.branch.is_permanently_closed) {
        return 'is-permanent';
      }
      return this.branch.status === 0 ? 'is-closed' : 'is-open';
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    fetchData() {
      this.isLoading = true;
      this.loadError = false;
      this.$axios.get('branch-status-history/' + this.branchId).then(res => {
        const data = res.data || {};
        this.branch = Object.assign({}, this.branch, data.branch || {});
        this.histories = Array.isArray(data.histories) ? data.histories : [];
        this.scanned = data.scanned || 0;
        this.truncated = !!data.truncated;
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
        this.loadError = true;
      });
    },

    statusLabel(value) {
      if (value === 1) return 'ເປີດ';
      if (value === 0) return 'ປິດ';
      return '-';
    },
    statusClass(value) {
      if (value === 1) return 'tag-open';
      if (value === 0) return 'tag-closed';
      return 'tag-none';
    },
    flagLabel(value) {
      if (value === true) return 'ປິດຖາວອນ';
      if (value === false) return 'ບໍ່';
      return '-';
    },
    flagClass(value) {
      if (value === true) return 'tag-permanent';
      if (value === false) return 'tag-none';
      return 'tag-none';
    },
    // What this save did, in one word — derived from the normalised to-values the backend sent.
    eventKind(item) {
      if (item.changed_fields.includes('is_permanently_closed') && item.is_permanently_closed.to === true) {
        return { label: 'ປິດຖາວອນ', cls: 'is-permanent', icon: 'fal fa-ban' };
      }
      if (item.changed_fields.includes('status') && item.status.to === 0) {
        return { label: 'ປິດຊົ່ວຄາວ', cls: 'is-closed', icon: 'fal fa-pause-circle' };
      }
      if (item.changed_fields.includes('status') && item.status.to === 1) {
        return { label: 'ເປີດສາຂາ', cls: 'is-open', icon: 'fal fa-check-circle' };
      }
      return { label: 'ປ່ຽນແປງ', cls: 'is-other', icon: 'fal fa-edit' };
    },
  },
};
</script>

<style lang="scss" scoped>
.status-history-panel {
  margin-top: 10px;

  .status-history-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .status-history-hint {
    color: #666;
    font-size: 14px;
    margin-top: 6px;
  }

  .status-history-card {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 14px 16px;
    height: 100%;

    &.is-open {
      border-color: #16a34a;
    }

    &.is-closed {
      border-color: #d97706;
    }

    &.is-permanent {
      border-color: #dc2626;
    }

    .status-history-value {
      font-size: 20px;
      font-weight: 600;
      margin-top: 4px;
    }

    .status-history-note {
      margin-top: 6px;
      font-size: 13px;
      color: #666;
    }
  }

  .status-history-table {
    margin-top: 10px;
  }

  .status-history-time {
    white-space: nowrap;
  }

  .status-history-arrow {
    margin: 0 8px;
    color: #999;
  }

  .is-changed {
    font-weight: 600;
  }

  .status-history-sub {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
  }

  .status-history-user-name {
    font-weight: 600;
  }

  .status-history-kind {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 13px;
    white-space: nowrap;

    &.is-open {
      background: #dcfce7;
      color: #166534;
    }

    &.is-closed {
      background: #fef3c7;
      color: #92400e;
    }

    &.is-permanent {
      background: #fee2e2;
      color: #991b1b;
    }

    &.is-other {
      background: #f3f4f6;
      color: #374151;
    }
  }

  .tag-open {
    color: #166534;
  }

  .tag-closed {
    color: #92400e;
  }

  .tag-permanent {
    color: #991b1b;
    font-weight: 600;
  }

  .tag-none {
    color: #666;
  }

  .status-history-empty {
    color: #888;
    text-align: center;
    padding: 18px 0;
  }
}
</style>
